import React from "react";
import { Container, Typography, Box, Divider } from "@mui/material";
import { useLanguage } from "../../contexts/LanguageContext";
import SEOHead from "../../components/SEOHead";

interface Section {
  heading: string;
  content: string | string[];
}

const ImpressumPage: React.FC = () => {
  const { currentLanguage, t } = useLanguage();

  const isAr = currentLanguage === "ar";

  const sections: Section[] = isAr
    ? [
        {
          heading: "معلومات وفق § 5 DDG",
          content: [
            "TOP Buchhaltungsservice UG (haftungsbeschränkt)",
            "Stuttgartstraße 15–17",
            "44143 Dortmund",
            "Deutschland",
          ],
        },
        {
          heading: "التمثيل",
          content: "المدير التنفيذي: Hasan Al Khdr",
        },
        {
          heading: "التواصل",
          content: [
            "الهاتف: +49 1575 5659651",
            "البريد الإلكتروني: info@top-bhs.de",
            "الموقع: https://www.top-bhs.de",
          ],
        },
        {
          heading: "التسجيل التجاري",
          content: [
            "محكمة التسجيل: Amtsgericht Dortmund",
            "رقم التسجيل: HRB 36764",
          ],
        },
        {
          heading: "رقم ضريبة القيمة المضافة",
          content: "رقم التعريف الضريبي وفق § 27a UStG: DE451330947",
        },
        {
          heading: "تسوية نزاعات المستهلك",
          content:
            "لسنا مستعدين ولا ملزمين بالمشاركة في إجراءات تسوية النزاعات أمام هيئة تحكيم المستهلك.",
        },
      ]
    : currentLanguage === "en"
      ? [
          {
            heading: "Information pursuant to § 5 DDG",
            content: [
              "TOP Buchhaltungsservice UG (haftungsbeschränkt)",
              "Stuttgartstraße 15–17",
              "44143 Dortmund",
              "Germany",
            ],
          },
          {
            heading: "Represented by",
            content: "Managing Director: Hasan Al Khdr",
          },
          {
            heading: "Contact",
            content: [
              "Phone: +49 1575 5659651",
              "Email: info@top-bhs.de",
              "Website: https://www.top-bhs.de",
            ],
          },
          {
            heading: "Commercial Register",
            content: [
              "Register court: Amtsgericht Dortmund",
              "Registration number: HRB 36764",
            ],
          },
          {
            heading: "VAT Identification Number",
            content: "VAT identification number pursuant to § 27a UStG: DE451330947",
          },
          {
            heading: "Consumer Dispute Resolution",
            content:
              "We are not willing and not obliged to participate in dispute resolution proceedings before a consumer arbitration board.",
          },
        ]
      : [
          {
            heading: "Angaben gemäß § 5 DDG",
            content: [
              "TOP Buchhaltungsservice UG (haftungsbeschränkt)",
              "Stuttgartstraße 15–17",
              "44143 Dortmund",
              "Deutschland",
            ],
          },
          {
            heading: "Vertreten durch den Geschäftsführer",
            content: "Hasan Al Khdr",
          },
          {
            heading: "Kontakt",
            content: [
              "Telefon: +49 1575 5659651",
              "E-Mail: info@top-bhs.de",
              "Website: https://www.top-bhs.de",
            ],
          },
          {
            heading: "Handelsregister",
            content: [
              "Registergericht: Amtsgericht Dortmund",
              "Registernummer: HRB 36764",
            ],
          },
          {
            heading: "Umsatzsteuer-Identifikationsnummer",
            content:
              "Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: DE451330947",
          },
          {
            heading: "Verbraucherstreitbeilegung",
            content:
              "Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
          },
        ];

  const title =
    currentLanguage === "ar"
      ? "البيانات القانونية"
      : currentLanguage === "en"
        ? "Imprint"
        : "Impressum";

  return (
    <>
      <SEOHead title={t('seo.impressum_title')} canonicalPath="/impressum" />
      <Container maxWidth="md" sx={{ py: 10, dir: isAr ? "rtl" : "ltr" }}>
      <Typography
        variant="overline"
        sx={{
          color: "text.secondary",
          letterSpacing: 3,
          display: "block",
          mb: 2,
        }}
      >
        {currentLanguage === "ar"
          ? "قانوني"
          : currentLanguage === "en"
            ? "Legal"
            : "Rechtliches"}
      </Typography>
      <Typography variant="h2" sx={{ mb: 6 }}>
        {title}
      </Typography>

      {sections.map((section, idx) => (
        <Box key={idx} sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            {section.heading}
          </Typography>
          {Array.isArray(section.content) ? (
            section.content.map((line, i) => (
              <Typography key={i} variant="body1" sx={{ mb: 0.5 }}>
                {line}
              </Typography>
            ))
          ) : (
            <Typography variant="body1">{section.content}</Typography>
          )}
          {idx < sections.length - 1 && <Divider sx={{ mt: 4 }} />}
        </Box>
      ))}
    </Container>
    </>
  );
};

export default ImpressumPage;
